import styles from './UserFilterManagementDialog.module.css';

import React, { useMemo, useState } from 'react';

import UserFilterFormDialog from 'components/ui/filter/UserFilterFormDialog';
import { DefaultDialogActions } from 'components/ui/FormUtils';
import Dialog from 'components/ui/Dialog';
import useBimContext from 'components/hooks/useBimContext';
import { BngIconButton } from 'components/bng/ui/BngIconButton';
import BngDropdown from 'components/bng/ui/BngDropdown';
import BngAvatar from 'components/bng/ui/BngAvatar';
import BngTable from 'components/bng/ui/BngTable';
import UiMsg from 'components/ui/UiMsg';
import Api from 'components/Api';
import useTranslation from 'components/hooks/useTranslation';
import useAsyncEffect from 'components/hooks/useAsyncEffect';
import Utils from 'components/Utils';
import LinkedObjects from 'components/ui/LinkedObjects';
import bngYup from 'components/bng/form/yup/BngYup';
import FilterOpts from 'components/bng/pages/common/filter/FilterOpts';
import FilterDropdown from 'components/bng/pages/common/filter/FilterDropdown';
import BngSearch from 'components/bng/ui/BngSearch';

const UserFilterDropdownSchema = bngYup((yup) => {
  return yup.object({
    user: yup.string().nullable().default(''),
    group: yup.string().nullable().default(''),
    role: yup.string().nullable().default(''),
  });
});

function UserFilterDropdown({ onChange = _.noop, initialValues = {}, users = [], groups = [] }) {
  const { t } = useTranslation();

  const usersOpts = useMemo(
    () =>
      FilterOpts({
        options: users
          .map((item) => ({
            value: item.user.id,
            label: Utils.Users.displayName(item.user),
          }))
          .sort((o1, o2) => Utils.Strings.compareIgnoreCase(o1.label, o2.label)),
        forceTitle: true,
      }),
    [users]
  );

  const groupsOpts = useMemo(
    () =>
      FilterOpts({
        options: groups
          .map((item) => ({
            value: item.id,
            label: item.name,
          }))
          .sort((o1, o2) => Utils.Strings.compareIgnoreCase(o1.label, o2.label)),
        forceTitle: true,
      }),
    [groups]
  );

  const roleOpts = useMemo(
    () =>
      FilterOpts({
        options: Utils.Project.getRolesWithMaster(),
        hasIcon: true,
      }),
    []
  );

  const initialFormValues = useMemo(() => _.merge({}, UserFilterDropdownSchema.default(), initialValues), [
    initialValues,
  ]);

  const filterFields = useMemo(
    () => [
      {
        name: 'user',
        label: t('user'),
        options: usersOpts,
      },
      {
        name: 'group',
        label: t('groups'),
        options: groupsOpts,
      },
      {
        name: 'role',
        label: t('user.type'),
        options: roleOpts,
      },
    ],
    [usersOpts, groupsOpts, roleOpts]
  );

  return (
    <FilterDropdown
      fields={filterFields}
      dropdownSchema={UserFilterDropdownSchema}
      initialFormValues={initialFormValues}
      onChange={onChange}
      popperClassName={styles.UserFilterDropdown}
    />
  );
}

export default function UserFilterManagementDialog({ closeModal = _.noop, filterId = null, afterSubmit = _.noop }) {
  const context = useBimContext();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState();
  const [userFilters, setUserFilters] = useState([]);
  const [usersAndGroups, setUsersAndGroups] = useState();
  const [selectedRowData, setSelectedRowData] = useState();
  const [filterButton, setFilterButton] = useState({
    searchTerm: '',
    filterButton: UserFilterDropdownSchema.default(),
  });

  const loadUsersAndGroups = async () => {
    setLoading(true);
    try {
      const data = await Api.Project.findProjectUsersAndGroups(context.project.id);
      setUsersAndGroups(data);
    } catch (e) {
      console.error('Error on loadUsersAndGroups()', e);
      UiMsg.ajaxError(t('user.group.fetch.error'), e);
    } finally {
      setLoading(false);
    }
  };

  useAsyncEffect({
    onMount: async () => {
      await loadUsersAndGroups();

      try {
        setLoading(true);
        const mdxGlobalFilter = await Api.MdxGlobalFilter.findOne(filterId);
        setFilter(mdxGlobalFilter);

        const uf = _.cloneDeep(_.orderBy(mdxGlobalFilter.userFilters, [(item) => item.accessConfig.id], ['asc']));
        uf.forEach((uf) => (uf._key = Utils.randomId()));
        setUserFilters(uf);
      } catch (e) {
        console.error('Error on fetching filter data onMount()', e);
        UiMsg.ajaxError(t('error.fetching.data.for.filters'), e);
      } finally {
        setLoading(false);
      }
    },
  });

  const openFormDialog = (row = null) => {
    setSelectedRowData({
      row,
    });
  };

  const cols = useMemo(() => {
    return [
      {
        key: 'id',
        label: t('id'),
        size: '10%',
        render: (row) => {
          return (
            <div className={styles.tableRows}>
              <span>{row.accessConfig.id}</span>
            </div>
          );
        },
      },
      {
        key: 'users',
        label: t('users'),
        size: '30%',
        onClick: (row) => openFormDialog(row),
        render: (row) => {
          const groups = row.accessConfig.groups.map((id) => usersAndGroups?.groups.find((group) => group.id === id));
          const users = row.accessConfig.users.map(
            (id) => usersAndGroups?.projectUsers.find((pu) => pu.user.id === id)?.user
          );

          const singleMember = groups.length + users.length === 1 ? groups[0] ?? users[0] : null;
          return (
            <div className="div-information-cockpit w-100">
              <span className="cockpit-item-slide-name w-100">
                {singleMember ? (
                  <div
                    title={`${singleMember.users ? t('group') : ''} ${Utils.Users.displayName(singleMember)}`.trim()}
                    className={styles.singleItemUserFilter}
                  >
                    <div>
                      <BngAvatar userId={singleMember.id} isGroup={!!singleMember.users} />
                    </div>
                    <div className={styles.singleItemUserFilterName}>
                      {Utils.Users.displayName(singleMember).trim()}
                    </div>
                  </div>
                ) : (
                  <LinkedObjects
                    itemWidth={40}
                    items={[...groups, ...users].filter((m) => m)}
                    render={(member) => {
                      const isGroup = member.hasOwnProperty('users');
                      return (
                        <div
                          key={`${isGroup ? 'GROUP' : 'USER'}-${member.id}`}
                          title={`${isGroup ? t('group') : ''} ${Utils.Users.displayName(member)}`.trim()}
                          className="gap-1"
                        >
                          <BngAvatar userId={member.id} isGroup={isGroup} />
                        </div>
                      );
                    }}
                  />
                )}
              </span>
            </div>
          );
        },
        rowClassName: styles.filterUsers,
      },
      {
        key: 'filteredMembers',
        label: t('filtered.members'),
        size: '50%',
        render: (row) => {
          const membersNames = row.memberReferences
            .map((fm) => fm.member)
            .sort((a, b) => Utils.Strings.compareIgnoreCase(a, b))
            .join(', ');
          return (
            <div className={styles.tableRows} title={membersNames}>
              {membersNames}
            </div>
          );
        },
      },
      {
        key: 'action',
        label: t('action'),
        size: '10%',
        render: (row) => {
          return (
            <div className={styles.tableRows}>
              <BngDropdown
                popperOpts={{ placement: 'bottom-end' }}
                overDialog
                options={[
                  {
                    label: t('edit_button'),
                    icon: 'edit',
                    onClick: () => openFormDialog(row),
                  },
                  {
                    label: t('delete_button'),
                    icon: 'close',
                    onClick: () => {
                      setUserFilters((prev) => prev.filter((r) => r._key !== row._key));
                    },
                  },
                ]}
              />
            </div>
          );
        },
      },
    ];
  }, [userFilters, usersAndGroups]);

  const filteredRows = useMemo(() => {
    let rows = userFilters || [];

    const selectedUserId = filterButton.filterButton?.user;
    if (selectedUserId) {
      rows = rows.filter((row) => {
        return row.accessConfig?.users?.includes(selectedUserId);
      });
    }

    const selectedGroupId = filterButton.filterButton?.group;
    if (selectedGroupId && usersAndGroups?.groups?.length > 0) {
      const selectedGroup = usersAndGroups.groups.find((group) => group.id === selectedGroupId);

      if (selectedGroup) {
        const groupUserIds = (selectedGroup.users || []).map((user) => user.id);

        rows = rows.filter((row) => {
          if (row.accessConfig?.groups?.includes(selectedGroupId)) {
            return true;
          }

          return row.accessConfig?.users?.some((userId) => groupUserIds.includes(userId));
        });
      } else {
        rows = [];
      }
    }

    const selectedRole = filterButton.filterButton?.role;
    if (selectedRole) {
      rows = rows.filter((row) => {
        const usersWithRole = row.accessConfig?.users
          ?.map((id) => usersAndGroups?.projectUsers.find((pu) => pu.user.id === id))
          .filter((pu) => pu?.role?.name === selectedRole);

        return usersWithRole.length > 0;
      });
    }

    if (filterButton.searchTerm) {
      const lowerSearchTerm = filterButton.searchTerm.toLowerCase();
      rows = rows.filter((row) => {
        let found = row.memberReferences.some((mr) => mr.member.toLowerCase().includes(lowerSearchTerm));

        if (!found && row.accessConfig?.users?.length > 0 && usersAndGroups?.projectUsers) {
          found = row.accessConfig.users.some((userId) => {
            const pu = usersAndGroups.projectUsers.find((users) => users.user.id === userId);
            if (pu && pu.user) {
              const nome = Utils.Users.displayName(pu.user).toLowerCase();
              const email = pu.user.email ? pu.user.email.toLowerCase() : '';
              return nome.includes(lowerSearchTerm) || email.includes(lowerSearchTerm);
            }
            return false;
          });
        }

        if (!found && row.accessConfig?.groups?.length > 0 && usersAndGroups?.groups) {
          found = row.accessConfig.groups.some((groupId) => {
            const group = usersAndGroups.groups.find((g) => g.id === groupId);
            if (group) {
              return group.name.toLowerCase().includes(lowerSearchTerm);
            }
            return false;
          });
        }

        return found;
      });
    }

    return rows;
  }, [userFilters, filterButton]);

  return (
    <>
      <Dialog
        className={`UserFilterManagementDialog ${styles.UserFilterManagementDialog} large`}
        title={t('user.filter.config', filter?.name)}
        loading={loading}
        onClose={closeModal}
      >
        <div className={'conection-dialog-division top'} />
        <Dialog.Body>
          <div className={styles.containerNewUserFilter}>
            <BngIconButton
              icon="add_circle"
              text={t('new.user.filter')}
              className={styles.buttonNewUserFilter}
              iconProps={{ style: { marginRight: '5px' } }}
              onClick={() => openFormDialog()}
            />
            <div className={'flex-center-items'}>
              <BngSearch
                alwaysOpen={false}
                className={styles.filterSearch}
                onChange={(val) => setFilterButton({ ...filterButton, searchTerm: val })}
              />
              <UserFilterDropdown
                onChange={(values) => {
                  setFilterButton({ ...filterButton, filterButton: values });
                }}
                initialValues={filterButton.filterButton}
                users={usersAndGroups?.projectUsers}
                groups={usersAndGroups?.groups}
              />
            </div>
          </div>
          <div className={'conection-dialog-division top'} />
          <div className={styles.tableContainer}>
            <BngTable cols={cols} rows={filteredRows} stickyHeader={true} rowKeyBuilder={(row) => row._key} />
          </div>
        </Dialog.Body>
        <Dialog.Footer>
          <DefaultDialogActions
            closeModal={closeModal}
            title={t('save')}
            onClickSaveButton={async () => {
              try {
                const filterCopy = _.cloneDeep(filter);
                filterCopy.userFilters = userFilters;
                await Api.MdxGlobalFilter.save(filterCopy);
                UiMsg.ok(t('save_success', [context.msg.t('filter2')]));
                closeModal();
                afterSubmit();
              } catch (e) {
                if (e.response?.status === 409) {
                  UiMsg.error(t('name_alredy_taken'));
                } else {
                  console.error(e);
                  UiMsg.error(t('error.creating.filter'), e);
                }
              }
            }}
          />
        </Dialog.Footer>
      </Dialog>

      {selectedRowData && (
        <UserFilterFormDialog
          closeModal={() => setSelectedRowData(null)}
          filter={filter}
          row={selectedRowData.row}
          usersAndGroups={usersAndGroups}
          onChange={({ groups, users, mdxMembers }) => {
            const row = selectedRowData.row
              ? _.cloneDeep(selectedRowData.row)
              : {
                  accessConfig: {},
                  memberReferences: [],
                  _key: Utils.randomId(),
                };
            row.accessConfig.groups = groups;
            row.accessConfig.users = users;

            row.memberReferences = _.orderBy(
              mdxMembers.map((member) => {
                const match = row.memberReferences.find((mr) => mr.member === member);
                if (match) {
                  return match;
                }
                return {
                  member,
                };
              }),
              ['member'],
              ['asc']
            );

            const copy = userFilters.slice();
            if (selectedRowData.row) {
              const idx = copy.findIndex((r) => r._key === selectedRowData.row._key);
              if (idx !== -1) {
                copy[idx] = row;
              }
            } else {
              copy.push(row);
            }
            setUserFilters(copy);
          }}
          onGroupChanged={loadUsersAndGroups}
        />
      )}
    </>
  );
}
